import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import {
  ACHIEVEMENT_CATEGORIES,
  ACHIEVEMENTS,
  AchievementCategoryCode,
  AchievementEntry,
} from './mockAchievements';
import AchievementTile from './components/AchievementTile';

type FilterMode = 'ALL' | 'UNLOCKED' | 'IN_PROGRESS';

const filterLabel: Record<FilterMode, string> = {
  ALL: 'Усі',
  UNLOCKED: 'Отримані',
  IN_PROGRESS: 'У процесі',
};

const sortByProgress = (a: AchievementEntry, b: AchievementEntry) => {
  if (a.unlocked !== b.unlocked) return a.unlocked ? -1 : 1;
  if (a.unlocked && b.unlocked) {
    return (b.unlockedAt ?? '').localeCompare(a.unlockedAt ?? '');
  }
  const aPct = a.current / a.threshold;
  const bPct = b.current / b.threshold;
  return bPct - aPct;
};

interface IAchievementsContentProps {
  showHeader?: boolean;
}

const AchievementsContent: React.FC<IAchievementsContentProps> = ({
  showHeader = true,
}) => {
  const [filter, setFilter] = useState<FilterMode>('ALL');

  const totals = useMemo(() => {
    const unlocked = ACHIEVEMENTS.filter((a) => a.unlocked).length;
    const total = ACHIEVEMENTS.length;
    const pct = total ? Math.round((unlocked / total) * 100) : 0;
    return { unlocked, total, pct };
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return ACHIEVEMENTS;
    if (filter === 'UNLOCKED') return ACHIEVEMENTS.filter((a) => a.unlocked);
    return ACHIEVEMENTS.filter((a) => !a.unlocked);
  }, [filter]);

  const grouped = useMemo(() => {
    const map = new Map<AchievementCategoryCode, AchievementEntry[]>();
    ACHIEVEMENT_CATEGORIES.forEach((c) => map.set(c.code, []));
    filtered.forEach((a) => map.get(a.category)?.push(a));
    map.forEach((entries) => entries.sort(sortByProgress));
    return map;
  }, [filtered]);

  return (
    <Stack spacing={3}>
      {showHeader ? (
        <Card>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={2}
              sx={{ flex: 1 }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'primary.purple',
                  borderRadius: '50%',
                  color: 'primary.main',
                  display: 'flex',
                  height: 60,
                  justifyContent: 'center',
                  width: 60,
                }}
              >
                <EmojiEventsOutlinedIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant={'h1'} sx={{ fontSize: { xs: 28, md: 38 } }}>
                  {'Нагороди'}
                </Typography>
                <Typography color={'text.secondary'} variant={'subtitle1'}>
                  {'Маленькі віхи, що відмічають твій прогрес.'}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ minWidth: { md: 280 }, width: { xs: '100%', md: 'auto' } }}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                sx={{ mb: 0.5 }}
              >
                <Typography variant={'body2'} color={'text.secondary'}>
                  {`Отримано ${totals.unlocked} з ${totals.total}`}
                </Typography>
                <Typography
                  variant={'body2'}
                  sx={{ color: 'primary.main', fontWeight: 700 }}
                >
                  {`${totals.pct}%`}
                </Typography>
              </Stack>
              <LinearProgress
                value={totals.pct}
                variant={'determinate'}
                sx={{
                  bgcolor: 'primary.purple',
                  borderRadius: 10,
                  height: 10,
                  '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
                }}
              />
            </Box>
          </Stack>
        </Card>
      ) : (
        <Box>
          <Typography variant={'h3'}>{'Нагороди'}</Typography>
          <Typography color={'text.secondary'}>
            {'Маленькі віхи, що відмічають твій прогрес.'}
          </Typography>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={{ mt: 2, mb: 0.5 }}
          >
            <Typography variant={'body2'} color={'text.secondary'}>
              {`Отримано ${totals.unlocked} з ${totals.total}`}
            </Typography>
            <Typography
              variant={'body2'}
              sx={{ color: 'primary.main', fontWeight: 700 }}
            >
              {`${totals.pct}%`}
            </Typography>
          </Stack>
          <LinearProgress
            value={totals.pct}
            variant={'determinate'}
            sx={{
              bgcolor: 'primary.purple',
              borderRadius: 10,
              height: 10,
              '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
            }}
          />
        </Box>
      )}

      <Stack direction={'row'} spacing={1} flexWrap={'wrap'}>
        {(['ALL', 'UNLOCKED', 'IN_PROGRESS'] as FilterMode[]).map((f) => (
          <Chip
            key={f}
            label={filterLabel[f]}
            clickable
            onClick={() => setFilter(f)}
            sx={(theme) => ({
              bgcolor:
                filter === f
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(155,124,255,0.18)'
                    : 'rgba(59,44,147,0.08)'
                  : 'transparent',
              border: `1px solid ${
                filter === f
                  ? theme.palette.primary.main
                  : theme.palette.mode === 'dark'
                  ? '#303A48'
                  : theme.palette.primary.tableBorder
              }`,
              color: filter === f ? 'primary.main' : 'text.primary',
              fontWeight: filter === f ? 700 : 500,
            })}
          />
        ))}
      </Stack>

      {ACHIEVEMENT_CATEGORIES.map((cat) => {
        const entries = grouped.get(cat.code) ?? [];
        if (entries.length === 0) return null;
        return (
          <Box key={cat.code}>
            <Box sx={{ mb: 2 }}>
              <Typography variant={'h3'}>{cat.title}</Typography>
              <Typography color={'text.secondary'} variant={'body2'}>
                {cat.description}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {entries.map((entry) => (
                <Grid key={entry.code} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <AchievementTile entry={entry} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
};

export default AchievementsContent;
